<template>
  <div class="home">
    <div class="page-container">
      <!-- Title -->
      <h1 class="site-title">THE HOLLOWAY</h1>
      
      <!-- Carousel -->
      <div class="carousel-wrapper">
        <Suspense>
          <ImageCarousel 
            :images="carouselImages" 
            :autoplay="true"
            :interval="6000"
            aspect-ratio="16/9"
          />
          <template #fallback>
            <div class="carousel-fallback">
              <img 
                src="/images/bin/holloway_upstairs.jpg" 
                alt="The Holloway" 
                class="fallback-image"
              />
              <div class="fallback-overlay">
                <p>Welcome to The Holloway</p>
              </div>
            </div>
          </template>
        </Suspense>
      </div>
      
      <!-- User's Booked Events (conditional) -->
      <section class="user-bookings" v-if="currentUser && userBookings.length > 0">
        <h2>Your Upcoming Events</h2>
        <div class="celtic-divider">
          <CelticBorder />
        </div>
        <div class="bookings-list">
          <div 
            v-for="booking in userBookings" 
            :key="booking.id"
            class="booking-item"
            @click="navigateToEvent(booking.eventId)"
          >
            <div class="booking-image">
              <img :src="booking.eventImage || '/images/placeholder-event.jpg'" :alt="booking.eventTitle">
            </div>
            <div class="booking-content">
              <div class="booking-date">{{ formatEventDate(booking.eventDate) }}</div>
              <div class="booking-title">{{ booking.eventTitle }}</div>
              <div class="booking-tickets">{{ booking.quantity }} {{ booking.quantity === 1 ? 'ticket' : 'tickets' }}</div>
            </div>
          </div>
        </div>
        <div class="view-all-events">
          <router-link to="/events" class="view-all-link">View All Events</router-link>
        </div>
      </section>
      
      <!-- Venue Info -->
      <section class="venue-info-section">
        <div class="definition">
          <p class="meaning">— (meaning <span class="quote">'A road or track that is significantly lower than the land on either side'</span>)</p>
          <p class="description">is a grassroots micro-venue in the heart of Norwich, Norfolk.</p>
        </div>
        <div class="venue-info">
          <p>Boasting a 60 capacity over two floors, hosting a variety of small-scale musical, literary and cinematic events in a relaxed, friendly and welcoming environment.</p>
          <p>The space houses a carefully-curated collection of books, zines, prints and music for sale, with a focus on publications and merchandise that relate to the artists, musicians, writers, filmmakers and others who perform, speak and screen with us.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import CelticBorder from '@/components/CelticBorder.vue'
import { defineAsyncComponent } from 'vue'
import { collection, getDocs, query, where, limit } from 'firebase/firestore'
import { db } from '@/firebase/config'

// Lazy load the image carousel component
const ImageCarousel = defineAsyncComponent({
  loader: () => import('@/components/ImageCarousel.vue'),
  // Add error handling for chunk loading failures
  onError(error, retry, fail, attempts) {
    if (error.message.includes('Loading chunk') && attempts <= 3) {
      console.log('Retrying chunk load, attempt ' + attempts)
      // Retry on chunk load errors
      setTimeout(() => {
        retry()
      }, 500)
    } else {
      // Log the error but don't fail completely
      console.error('Error loading ImageCarousel component:', error)
      // Still allow the component to render, fallback will be shown
    }
  }
})

const store = useStore()
const router = useRouter()

// Get current user from store
const currentUser = computed(() => store.state.auth.user)

// Get upcomingEvents from store using computed
const upcomingEvents = computed(() => store.getters['events/upcomingEvents'])

// Create a ref to store user's bookings with event details
const userBookings = ref([])
// Add loading state
const isLoadingBookings = ref(false)

// Define carousel images
const carouselImages = ref([
  {
    src: '/images/bin/holloway_upstairs.jpg',
    alt: 'The Holloway Upstairs',
    loading: 'lazy' // Add lazy loading attribute
  },
  {
    src: '/images/bin/the_front.jpg',
    alt: 'The Holloway Front',
    loading: 'lazy' // Add lazy loading attribute
  },
  {
    src: '/images/bin/outside.jpeg',
    alt: 'The Holloway Front',
    loading: 'lazy' // Add lazy loading attribute
  }
])

// Function to format the event date
const formatEventDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// Function to navigate to event details
const navigateToEvent = (eventId) => {
  router.push({ name: 'EventDetails', params: { id: eventId } })
}

// Function to fetch user's bookings with details - optimized
const fetchUserBookings = async () => {
  if (!currentUser.value || isLoadingBookings.value) {
    return
  }
  
  try {
    isLoadingBookings.value = true
    
    // Get only upcoming bookings - add a limit
    const userBookingsRef = collection(db, `users/${currentUser.value.uid}/bookings`)
    const bookingsSnapshot = await getDocs(userBookingsRef)
    
    if (bookingsSnapshot.empty) {
      userBookings.value = []
      isLoadingBookings.value = false
      return
    }
    
    // Extract event IDs to fetch them in one go
    const eventIds = bookingsSnapshot.docs.map(doc => doc.data().eventId)
    
    // Use the already fetched upcomingEvents if available
    let eventsData = upcomingEvents.value
    
    // Map bookings with event data
    const bookingsData = bookingsSnapshot.docs
      .map(doc => {
        const booking = doc.data()
        const event = eventsData.find(e => e.id === booking.eventId)
        
        if (!event) return null
        
        return {
          id: doc.id,
          eventId: booking.eventId,
          eventTitle: event.title,
          eventDate: event.date,
          eventImage: event.imageUrl,
          quantity: booking.quantity || 1,
        }
      })
      .filter(Boolean) // Remove any null entries
    
    // Sort by date
    bookingsData.sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    
    userBookings.value = bookingsData
  } catch (error) {
    console.error('Error fetching user bookings:', error)
    userBookings.value = []
  } finally {
    isLoadingBookings.value = false
  }
}

// Function to load Firebase data after initial render
const loadFirebaseData = async () => {
  try {
    // Load events first
    await store.dispatch('events/fetchEvents')
    // Then fetch user bookings only once if user is logged in
    if (currentUser.value) {
      fetchUserBookings()
    }
  } catch (error) {
    console.error('Error loading Firebase data:', error)
  }
}

// Optimize initialization to load page immediately
onMounted(() => {
  // Defer Firebase operations until after initial render
  nextTick(() => {
    loadFirebaseData()
  })
})

// Optimize watcher to prevent redundant fetches
watch(currentUser, (newUser, oldUser) => {
  // Only fetch if the user changed from null to a value (just logged in)
  if (newUser && !oldUser) {
    fetchUserBookings()
  } else if (!newUser && oldUser) {
    // User logged out
    userBookings.value = []
  }
})

// Only refetch bookings when upcomingEvents change and we already have bookings
watch(upcomingEvents, (newEvents) => {
  if (currentUser.value && userBookings.value.length > 0 && newEvents.length > 0) {
    fetchUserBookings()
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/main.scss';

/* Main styles */
.home {
  width: 100%;
  overflow-x: hidden;
  background-color: rgb(242, 253, 247);
  padding: 2rem 0;
  position: relative;
  z-index: 1;
  
  .page-container {
    width: 100%;
    max-width: 1200px;
    padding: 0 $spacing-sm;
    box-sizing: border-box;
    margin: 0 auto;
    position: relative;
    z-index: 2;
    
    /* Make the container a flexbox with consistent gap */
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    
    @media (min-width: $breakpoint-md) {
      padding: 0 $spacing-md;
      gap: 3rem;
    }
  }
  
  .site-title {
    font-family: $font-family-main;
    font-size: 3.5rem;
    color: $tertiary-color;
    line-height: 1.1;
    font-weight: bold;
    margin: 0;
    text-align: center;
    
    @media (min-width: $breakpoint-md) {
      font-size: 5rem;
    }
  }
  
  .carousel-wrapper {
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    overflow: hidden;
    
    /* Safari-specific fixes */
    @media not all and (min-resolution:.001dpcm) { 
      @supports (-webkit-appearance:none) {
        max-width: 100%;
        width: 100%;
        display: block;
        
        .carousel-container {
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
        }
        
        .carousel-slide {
          width: 100% !important;
        }
        
        img.carousel-image {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
      }
    }
  }
  
  .venue-info-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 800px;
    margin: 0 auto;
    
    .definition {
      .meaning {
        font-size: 1rem;
        color: $tertiary-color;
        margin-bottom: 0.5rem;
        
        .quote {
          font-style: italic;
        }
      }
      
      .description {
        font-size: 1rem;
        color: $tertiary-color;
        margin: 0;
      }
    }
    
    .venue-info {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      
      p {
        font-size: 1rem;
        color: $tertiary-color;
        line-height: 1.6;
        margin: 0;
      }
    }
  }
  
  .user-bookings {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    
    h2 {
      font-size: 2rem;
      color: $tertiary-color;
      margin: 0;
      text-align: center;
    }
    
    .celtic-divider {
      width: 100%;
      max-width: 300px;
      margin: 0 auto;
      
      svg {
        width: 100%;
        height: auto;
        fill: $tertiary-color;
      }
    }
    
    .bookings-list {
      max-width: 600px;
      margin: 0 auto;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      
      @media (max-width: $breakpoint-sm) {
        max-width: 100%;
        padding: 0 0.5rem;
      }
    }
    
    .booking-item {
      display: flex;
      align-items: stretch;
      margin: 0;
      background-color: rgba(255, 255, 255, 0.8);
      border-left: 4px solid $tertiary-color;
      border-radius: 0 4px 4px 0;
      cursor: pointer;
      transition: all 0.2s ease;
      overflow: hidden;
      
      @media (max-width: $breakpoint-sm) {
        border-left: 3px solid $tertiary-color;
        border-radius: 0 4px 4px 0;
      }
      
      &:hover {
        background-color: rgba(255, 255, 255, 1);
        transform: translateX(5px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        
        @media (max-width: $breakpoint-sm) {
          transform: translateX(3px);
        }
      }
      
      .booking-image {
        width: 80px;
        min-width: 80px;
        height: auto;
        
        @media (max-width: $breakpoint-sm) {
          width: 60px;
          min-width: 60px;
          height: 60px;
        }
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          
          @media (max-width: $breakpoint-sm) {
          }
        }
      }
      
      .booking-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 1rem;
        flex: 1;
        gap: 0.5rem;
        
        @media (max-width: $breakpoint-sm) {
          padding: 0.75rem;
        }
      }
      
      .booking-date {
        font-size: 0.9rem;
        color: $text-color;
        font-style: italic;
        margin: 0;
        
        @media (max-width: $breakpoint-sm) {
          font-size: 0.85rem;
        }
      }
      
      .booking-title {
        font-weight: 600;
        color: $tertiary-color;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        
        @media (max-width: $breakpoint-sm) {
          white-space: normal;
          font-size: 1.1rem;
        }
      }
      
      .booking-tickets {
        font-size: 0.9rem;
        color: $text-color;
        background-color: rgba($tertiary-color, 0.1);
        padding: 0.3rem 0.6rem;
        border-radius: 20px;
        align-self: flex-start;
        margin: 0;
        
        @media (max-width: $breakpoint-sm) {
          align-self: flex-start;
          font-size: 0.8rem;
          margin-top: 0;
        }
      }
    }
    
    .view-all-events {
      margin: 0;
      text-align: center;
      
      .view-all-link {
        display: inline-block;
        padding: 0.75rem 1.5rem;
        background-color: $tertiary-color;
        color: white;
        text-decoration: none;
        border-radius: 4px;
        font-weight: 500;
        transition: background-color 0.3s, transform 0.2s;
        
        &:hover {
          background-color: darken($tertiary-color, 10%);
          transform: translateY(-2px);
        }
      }
    }
  }
  
  .carousel-fallback {
    height: 300px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #f8f8f8;
    color: $tertiary-color;
    font-style: italic;
    position: relative;
    overflow: hidden;
    
    .fallback-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .fallback-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 1rem;
      
      p {
        font-size: 1.5rem;
        color: white;
        text-align: center;
        margin: 0;
      }
    }
  }
}
</style> 